import {
  User,
  Mail,
  MapPin,
  Building,
  Briefcase,
  DollarSign,
  Twitter,
  Linkedin,
  CheckCircle,
  Loader,
  Camera,
} from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { Card, CardContent } from '@/components/ui/card'
import type { ProfileScreenViewProps } from './ProfileScreenViewProps'

export const ProfileScreenMobile = ({
  profile,
  formData,
  uploadingPicture,
  updating,
  validationErrors,
  fileInputRef,
  getProfilePictureUrl,
  getUserInitials,
  onProfilePictureChange,
  onInputChange,
  onSubmit,
  mobileInputClass,
  hasMinAmount,
  hasMaxAmount,
  countryDisplay,
  formatCurrencyDesktop,
  industryLabelMap,
}: ProfileScreenViewProps) => {
  if (!profile) return null

  const mobileSectionTitleClass =
    'font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[var(--text)]'
  const mobileFieldLabelClass =
    'block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[var(--text)]'
  const mobileHintTextClass =
    'font-plus-jakarta text-[12px] font-normal leading-[16px] text-[var(--text)] opacity-60'

  return (
    <div className="space-y-3">
      <Card
        padding="none"
        className="rounded-none border border-white bg-[rgba(255,255,255,0.32)] dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]"
      >
        <CardContent className="px-3 py-3">
          <form onSubmit={onSubmit} className="space-y-0">
            <div className="space-y-4 pb-4">
              <p className={mobileSectionTitleClass}>
                Basic Information
              </p>

              <div className="flex items-center gap-3">
                <div className="relative group">
                  <div className="h-16 w-16 overflow-hidden rounded-full border border-[rgba(45,41,35,0.12)] bg-[var(--background-lighter)] dark:border-[#171717]">
                    {getProfilePictureUrl() ? (
                      <img src={getProfilePictureUrl() || ''} alt="Profile" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-primary/10">
                        <span className="text-xl font-semibold text-primary">{getUserInitials()}</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed"
                  >
                    {uploadingPicture ? (
                      <Loader className="h-5 w-5 animate-spin text-white" />
                    ) : (
                      <Camera className="h-5 w-5 text-white" />
                    )}
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onProfilePictureChange}
                    className="hidden"
                  />
                </div>

                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[var(--text)] underline underline-offset-2 disabled:opacity-50"
                  >
                    {uploadingPicture ? 'Uploading...' : 'Change photo'}
                  </button>
                  <p className={mobileHintTextClass}>
                    JPEG, PNG or WebP. Max 25MB.
                  </p>
                  {validationErrors.profile_picture && (
                    <p className="text-[12px] leading-[16px] text-red-500">{validationErrors.profile_picture}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    First name
                  </label>
                  <Input
                    type="text"
                    value={formData.first_name || ''}
                    onChange={(e) => onInputChange('first_name', e.target.value)}
                    placeholder="Enter your first name"
                    className={mobileInputClass}
                    leftElement={<User className="h-[14px] w-[14px]" />}
                  />
                </div>
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Last Name
                  </label>
                  <Input
                    type="text"
                    value={formData.last_name || ''}
                    onChange={(e) => onInputChange('last_name', e.target.value)}
                    placeholder="Enter your last name"
                    className={mobileInputClass}
                    leftElement={<User className="h-[14px] w-[14px]" />}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-[rgba(45,41,35,0.12)] py-4 dark:border-[#171717]">
              <p className={mobileSectionTitleClass}>
                Contact Information
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => onInputChange('email', e.target.value)}
                    className={mobileInputClass}
                    leftElement={<Mail className="h-[14px] w-[14px]" />}
                    error={validationErrors.email}
                  />
                </div>
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Country
                  </label>
                  <Input
                    type="text"
                    value={formData.country || ''}
                    onChange={(e) => onInputChange('country', e.target.value)}
                    placeholder="Enter your country"
                    className={mobileInputClass}
                    leftElement={<MapPin className="h-[14px] w-[14px]" />}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-[rgba(45,41,35,0.12)] py-4 dark:border-[#171717]">
              <p className={mobileSectionTitleClass}>
                Professional Information
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Company Name
                  </label>
                  <Input
                    type="text"
                    value={formData.company_name || ''}
                    onChange={(e) => onInputChange('company_name', e.target.value)}
                    placeholder="Enter your company"
                    className={mobileInputClass}
                    leftElement={<Building className="h-[14px] w-[14px]" />}
                  />
                </div>
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Job Title
                  </label>
                  <Input
                    type="text"
                    value={formData.job_title || ''}
                    onChange={(e) => onInputChange('job_title', e.target.value)}
                    placeholder="Enter your job title"
                    className={mobileInputClass}
                    leftElement={<Briefcase className="h-[14px] w-[14px]" />}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-[rgba(45,41,35,0.12)] py-4 dark:border-[#171717]">
              <p className={mobileSectionTitleClass}>
                Investment Preferences
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Minimum Investment Amount
                  </label>
                  <Input
                    type="number"
                    value={formData.preferred_investment_amount_min || ''}
                    onChange={(e) => onInputChange('preferred_investment_amount_min', parseFloat(e.target.value))}
                    placeholder="10 000"
                    className={mobileInputClass}
                    leftElement={<DollarSign className="h-4 w-4" />}
                  />
                </div>
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    Maximum Investment Amount
                  </label>
                  <Input
                    type="number"
                    value={formData.preferred_investment_amount_max || ''}
                    onChange={(e) => onInputChange('preferred_investment_amount_max', parseFloat(e.target.value))}
                    placeholder="20 000 000"
                    className={mobileInputClass}
                    leftElement={<DollarSign className="h-4 w-4" />}
                    error={validationErrors.preferred_investment_amount_max}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 border-t border-[rgba(45,41,35,0.12)] py-4 dark:border-[#171717]">
              <p className={mobileSectionTitleClass}>
                Social Accounts
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    X Account
                    {profile.x_account_verified && (
                      <CheckCircle className="ml-1 inline h-4 w-4 text-green-500" />
                    )}
                  </label>
                  <Input
                    type="text"
                    value={formData.x_account || ''}
                    onChange={(e) => onInputChange('x_account', e.target.value)}
                    placeholder="@username"
                    className={mobileInputClass}
                    leftElement={<Twitter className="h-3 w-3" />}
                    error={validationErrors.x_account}
                  />
                </div>
                <div className="space-y-2">
                  <label className={mobileFieldLabelClass}>
                    LinkedIn
                    {profile.linkedin_account_verified && (
                      <CheckCircle className="ml-1 inline h-4 w-4 text-green-500" />
                    )}
                  </label>
                  <Input
                    type="url"
                    value={formData.linkedin || ''}
                    onChange={(e) => onInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/username"
                    className={mobileInputClass}
                    leftElement={<Linkedin className="h-[14px] w-[14px]" />}
                    error={validationErrors.linkedin}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center border-t border-[rgba(45,41,35,0.12)] pt-5 dark:border-[#171717]">
              <Button
                type="submit"
                variant="primary"
                disabled={updating}
                isLoading={updating}
                className="!h-[48px] !rounded-none !bg-[#2d2923] !px-4 font-plus-jakarta text-[14px] font-medium leading-[20px] !text-white hover:!bg-[#2d2923] dark:!bg-[#fdfdfd] dark:!text-[#2d2923] dark:hover:!bg-[#fdfdfd]"
              >
                {updating ? (
                  'Updating...'
                ) : (
                  <span className="inline-flex items-center gap-3">
                    <span>UPDATE PROFILE</span>
                    <span className="inline-flex items-center border border-[rgba(255,255,255,0.16)] bg-[rgba(255,255,255,0.12)] px-[6px] py-[4px] font-plus-jakarta text-[12px] font-normal leading-[16px] text-white dark:border-[rgba(45,41,35,0.16)] dark:bg-[rgba(45,41,35,0.12)] dark:text-[#2d2923]">
                      Press Enter
                    </span>
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
          <div className="absolute left-6 top-6 flex h-[76px] w-[calc(100%-48px)] flex-col justify-between">
            <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Investment Range
            </p>
            {hasMinAmount || hasMaxAmount ? (
              <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                {hasMinAmount ? (
                  <>
                    <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                    <span>{` ${formatCurrencyDesktop(profile.preferred_investment_amount_min).replace('$', '')}`}</span>
                  </>
                ) : (
                  <span>Not specified</span>
                )}
                <span>{` - `}</span>
                {hasMaxAmount ? (
                  <>
                    <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                    <span>{` ${formatCurrencyDesktop(profile.preferred_investment_amount_max).replace('$', '')}`}</span>
                  </>
                ) : (
                  <span>Not specified</span>
                )}
              </p>
            ) : (
              <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                Not specified
              </p>
            )}
          </div>
        </div>

        <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
          <div className="absolute left-6 top-6 flex h-[76px] w-[calc(100%-48px)] flex-col justify-between">
            <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Company
            </p>
            <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              {profile.company_name || 'Not specified'}
            </p>
          </div>
        </div>

        <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
          <div className="absolute left-6 top-6 flex h-[76px] w-[calc(100%-48px)] flex-col justify-between">
            <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Location
            </p>
            <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              {countryDisplay}
            </p>
          </div>
        </div>

        <div className="relative h-[124px] bg-white dark:bg-[#1c1c1c]">
          <div className="absolute left-6 top-6 flex h-[76px] w-[calc(100%-48px)] flex-col justify-between">
            <p className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
              Industries
            </p>
            <p className="font-plus-jakarta text-[23px] font-medium leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              {(profile.industries_of_interest || []).length}
            </p>
          </div>
        </div>
      </div>

      <div className="border border-white bg-[rgba(255,255,255,0.32)] p-4 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <p className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
          Investment Preferences
        </p>

        <div className="mt-4 space-y-3">
          <div className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              Buy Interest
            </p>
            <div className="mt-[14px] flex flex-wrap gap-[10px]">
              {(formData.companies_of_interest_buy || []).length > 0 ? (
                (formData.companies_of_interest_buy || []).map((item) => (
                  <span
                    key={`mobile-profile-buy-${item}`}
                    className="inline-flex h-[20px] items-center justify-center border border-[#1fb845] bg-[rgba(255,255,255,0.4)] px-2 font-plus-jakarta text-[12px] font-semibold leading-[16px] uppercase text-[#1fb845] dark:bg-[rgba(28,28,28,0.4)]"
                  >
                    {item}
                  </span>
                ))
              ) : (
                <span className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Not specified
                </span>
              )}
            </div>
          </div>

          <div className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              Sell Interest
            </p>
            <div className="mt-[14px] flex flex-wrap gap-[10px]">
              {(formData.companies_of_interest_sell || []).length > 0 ? (
                (formData.companies_of_interest_sell || []).map((item) => (
                  <span
                    key={`mobile-profile-sell-${item}`}
                    className="inline-flex h-[20px] items-center justify-center border border-[#ff540c] bg-[rgba(255,255,255,0.4)] px-2 font-plus-jakarta text-[12px] font-semibold leading-[16px] uppercase text-[#ff540c] dark:bg-[rgba(28,28,28,0.4)]"
                  >
                    {item}
                  </span>
                ))
              ) : (
                <span className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Not specified
                </span>
              )}
            </div>
          </div>

          <div className="border border-[rgba(45,41,35,0.12)] bg-white px-5 py-4 dark:border-[rgba(253,253,253,0.12)] dark:bg-[#1c1c1c]">
            <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              Industries
            </p>
            <div className="mt-[14px] flex flex-wrap gap-[10px]">
              {(formData.industries_of_interest || []).length > 0 ? (
                (formData.industries_of_interest || []).map((industry) => (
                  <span
                    key={`mobile-profile-industry-${industry}`}
                    className="inline-flex h-[20px] items-center justify-center border border-[rgba(45,41,35,0.32)] px-2 font-plus-jakarta text-[12px] font-medium leading-[16px] uppercase text-[rgba(45,41,35,0.72)] dark:border-[rgba(253,253,253,0.32)] dark:text-[rgba(253,253,253,0.72)]"
                  >
                    {industryLabelMap[industry] || industry}
                  </span>
                ))
              ) : (
                <span className="font-plus-jakarta text-[12px] font-medium leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Not specified
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
